/**
 * The editor standings, the page's main event, drawn like a sports table.
 *
 * Every editor has the same few things: a place, a face (their initial in
 * their roster colour), a name, the bonus so far and the share of the pool it
 * is, and - small, underneath - the spend and the videos (or concepts) the
 * bonus is made of. The share is drawn as one bar, measured against the pool's
 * leader, so the gap between places is something you can see.
 *
 * The top three stand on a podium: three cards, first in the middle and
 * raised, each with a medal ring round the face and its place on a pedestal.
 * Everyone from fourth down is one quiet row of a table with a steady rhythm.
 * The signed-in editor is marked "You" wherever they are.
 *
 * Every card and row opens the editor's page.
 */
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ChevronRightIcon } from "lucide-react";
import {
  formatMoney,
  formatMoneyCompact,
  formatPercent,
  type BonusTerms,
  type LeaderboardEditor,
} from "@scandi/shared";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { initials } from "@/features/auth/initials";
import { useSession } from "@/hooks/use-session";
import { cn } from "@/lib/utils";
import { rangeToParams, type RangeSelection } from "@/lib/api";

const MEDAL = {
  1: { ring: "ring-gold", text: "gold-text", fill: "gold-gradient", pedestal: "from-gold/30 to-gold/5", glow: "shadow-[0_18px_40px_-20px_var(--gold)]" },
  2: { ring: "ring-silver", text: "text-silver", fill: "bg-silver", pedestal: "from-silver/30 to-silver/5", glow: "" },
  3: { ring: "ring-bronze", text: "text-bronze", fill: "bg-bronze", pedestal: "from-bronze/30 to-bronze/5", glow: "" },
} as const;
type Place = keyof typeof MEDAL;

interface Shared {
  currency: string;
  range: RangeSelection;
  showBonus: boolean;
  byConcept: boolean;
  viewerId: number | null;
  topShare: number;
}

export function StandingsBoard({
  standings,
  terms,
  currency,
  range,
  showBonus,
}: {
  standings: LeaderboardEditor[];
  terms: BonusTerms;
  currency: string;
  range: RangeSelection;
  showBonus: boolean;
}) {
  const { editor: viewer } = useSession();
  const shared: Shared = {
    currency,
    range,
    showBonus,
    byConcept: terms.outputUnit === "concept",
    viewerId: viewer?.id ?? null,
    topShare: Math.max(0, ...standings.map((s) => s.share)),
  };
  // A podium only for places actually earned: nobody stands on it with nothing.
  const podium = standings.filter((s) => s.rank <= 3 && s.share > 0);
  const rest = standings.filter((s) => !podium.includes(s));

  return (
    <div className="flex min-w-0 flex-col gap-3">
      {podium.length > 0 && (
        <ol className="grid grid-cols-3 items-end gap-2 sm:gap-3">
          {/* Second, first, third: the winner in the middle, raised. */}
          {[podium[1], podium[0], podium[2]].map((editor, i) =>
            editor ? <PodiumCard key={editor.editorId} editor={editor} {...shared} /> : <li key={`empty-${i}`} aria-hidden />,
          )}
        </ol>
      )}
      {rest.length > 0 && (
        <ol className="border-border/70 bg-card min-w-0 overflow-hidden rounded-[22px] border">
          {rest.map((editor) => (
            <TableRow key={editor.editorId} editor={editor} {...shared} />
          ))}
        </ol>
      )}
    </div>
  );
}

/** One of the top three: a card standing on its pedestal. */
function PodiumCard({ editor, currency, range, showBonus, byConcept, viewerId, topShare }: { editor: LeaderboardEditor } & Shared) {
  const { t } = useTranslation();
  const place = editor.rank as Place;
  const medal = MEDAL[place];
  const first = place === 1;

  return (
    <li className={cn("min-w-0", first ? "order-2" : place === 2 ? "order-1" : "order-3")}>
      <Link
        to={`/editors/${editor.editorId}?${rangeToParams(range)}`}
        aria-label={t("leaderboard.viewEditorVideos", { name: editor.editor })}
        className={cn(
          "group/pod bg-card border-border/70 focus-visible:ring-ring/50 flex min-w-0 flex-col items-center overflow-hidden rounded-[22px] border text-center transition-[transform,box-shadow] duration-200 outline-none hover:-translate-y-0.5 focus-visible:ring-2 motion-reduce:hover:translate-y-0",
          medal.glow,
          viewerId === editor.editorId && "border-brand/50",
        )}
      >
        <div className={cn("flex w-full min-w-0 flex-col items-center gap-2 px-2 sm:px-4", first ? "pt-6 sm:pt-8" : "pt-4 sm:pt-5")}>
          <Avatar name={editor.editor} color={editor.color} className={cn("ring-offset-card ring-[3px] ring-offset-2", medal.ring, first ? "size-14 text-xl sm:size-20 sm:text-3xl" : "size-11 text-base sm:size-14 sm:text-xl")} />
          <span className="flex min-w-0 max-w-full flex-wrap items-center justify-center gap-1.5">
            <span className={cn("truncate font-bold", first ? "text-base sm:text-xl" : "text-sm sm:text-lg")}>{editor.editor}</span>
            {viewerId === editor.editorId && <YouBadge />}
          </span>
          {showBonus ? (
            <span className={cn("text-success leading-none font-black tracking-tight tabular-nums", first ? "text-2xl sm:text-5xl" : "text-xl sm:text-4xl")}>
              {money(editor.projectedBonus, currency)}
            </span>
          ) : (
            <span className={cn("leading-none font-black tabular-nums", first ? "text-2xl sm:text-4xl" : "text-xl sm:text-3xl")}>{formatPercent(editor.share)}</span>
          )}
          <ShareBar share={editor.share} fill={topShare > 0 ? editor.share / topShare : 0} className={medal.fill} showLabel={showBonus} />
          <Facts editor={editor} currency={currency} byConcept={byConcept} className="hidden justify-center sm:flex" />
        </div>
        {/* The pedestal: first stands tallest. */}
        <div
          className={cn(
            "mt-3 flex w-full items-center justify-center bg-gradient-to-b font-black tabular-nums",
            medal.pedestal,
            first ? "h-14 text-4xl sm:h-20 sm:text-5xl" : place === 2 ? "h-10 text-3xl sm:h-14 sm:text-4xl" : "h-8 text-2xl sm:h-10 sm:text-3xl",
          )}
        >
          <span className={medal.text}>{place}</span>
        </div>
      </Link>
    </li>
  );
}

/** Fourth place down: one row of the table. */
function TableRow({ editor, currency, range, showBonus, byConcept, viewerId, topShare }: { editor: LeaderboardEditor } & Shared) {
  const { t } = useTranslation();
  const isViewer = viewerId === editor.editorId;

  return (
    <li className="border-b last:border-b-0">
      <Link
        to={`/editors/${editor.editorId}?${rangeToParams(range)}`}
        aria-label={t("leaderboard.viewEditorVideos", { name: editor.editor })}
        className={cn(
          "group/row hover:bg-accent/50 focus-visible:ring-ring/50 grid grid-cols-[1.75rem_auto_minmax(0,1fr)_auto] items-center gap-x-3 px-3 py-3 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-inset sm:grid-cols-[2.5rem_auto_minmax(0,1fr)_minmax(0,12rem)_6.5rem_1rem] sm:gap-x-4 sm:px-5",
          isViewer && "bg-brand/[0.07]",
        )}
      >
        <span className="text-muted-foreground/70 text-center text-xl font-black tabular-nums sm:text-2xl">{editor.rank}</span>
        <Avatar name={editor.editor} color={editor.color} className="size-9 text-sm sm:size-10" />
        <span className="flex min-w-0 flex-col gap-0.5">
          <span className="flex min-w-0 items-center gap-1.5">
            <span className="truncate text-[15px] font-bold">{editor.editor}</span>
            {isViewer && <YouBadge />}
            {!editor.qualified && <NotQualified />}
          </span>
          <Facts editor={editor} currency={currency} byConcept={byConcept} />
        </span>
        <ShareBar share={editor.share} fill={topShare > 0 ? editor.share / topShare : 0} className="bg-foreground/35" showLabel={showBonus} wrapperClassName="hidden sm:flex" />
        <span className="flex flex-col items-end">
          {showBonus ? (
            <>
              <span className={cn("text-xl leading-none font-black tabular-nums", editor.projectedBonus > 0 ? "text-success" : "text-muted-foreground")}>
                {money(editor.projectedBonus, currency)}
              </span>
              <span className="text-muted-foreground text-[11.5px] font-semibold tabular-nums sm:hidden">{formatPercent(editor.share)}</span>
            </>
          ) : (
            <span className="text-xl leading-none font-black tabular-nums">{formatPercent(editor.share)}</span>
          )}
        </span>
        <ChevronRightIcon className="text-muted-foreground/40 group-hover/row:text-foreground hidden size-4 sm:block" aria-hidden />
      </Link>
    </li>
  );
}

/** The editor's initial on their roster colour. */
function Avatar({ name, color, className }: { name: string; color: string; className?: string }) {
  return (
    <span
      aria-hidden
      className={cn("flex shrink-0 items-center justify-center rounded-full font-black text-white shadow-sm", className)}
      style={{ backgroundColor: color }}
    >
      {initials(name).slice(0, 1)}
    </span>
  );
}

/** The share of the pool as a bar measured against the leader's, with the number beside it. */
function ShareBar({
  share,
  fill,
  className,
  showLabel,
  wrapperClassName,
}: {
  share: number;
  fill: number;
  className: string;
  showLabel: boolean;
  wrapperClassName?: string;
}) {
  const { t } = useTranslation();
  return (
    <span className={cn("flex w-full min-w-0 flex-col gap-1", wrapperClassName)}>
      <span className="bg-foreground/[0.07] h-1.5 w-full overflow-hidden rounded-full" aria-hidden>
        <span className={cn("block h-full rounded-full transition-[width] duration-700 ease-out", className)} style={{ width: `${Math.max(fill * 100, fill > 0 ? 4 : 0)}%` }} />
      </span>
      {showLabel && (
        <span className="text-muted-foreground text-[11.5px] font-semibold tabular-nums">{t("bonus.row.ofPool", { share: formatPercent(share) })}</span>
      )}
    </span>
  );
}

/** Spend and launches, the two things the bonus is made of: small. */
function Facts({ editor, currency, byConcept, className }: { editor: LeaderboardEditor; currency: string; byConcept: boolean; className?: string }) {
  const { t } = useTranslation();
  return (
    <span className={cn("text-muted-foreground flex min-w-0 flex-wrap items-center gap-x-2.5 text-[12.5px] tabular-nums", className)}>
      <span>
        <span className="text-foreground/80 font-semibold">{formatMoneyCompact(editor.spend, currency)}</span> {t("bonus.row.spend")}
      </span>
      <span>
        <span className="text-foreground/80 font-semibold">{editor.output}</span>{" "}
        {byConcept ? t("bonus.row.concepts", { count: editor.output }) : t("bonus.row.videos", { count: editor.output })}
      </span>
    </span>
  );
}

function YouBadge() {
  const { t } = useTranslation();
  return <Badge className="bg-brand/12 text-brand border-brand/25 h-5 shrink-0 px-1.5 text-[10px]">{t("bonus.row.you")}</Badge>;
}

function NotQualified() {
  const { t } = useTranslation();
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge variant="outline" className="h-5 shrink-0 px-1.5 text-[10px]">
          {t("leaderboard.notQualified")}
        </Badge>
      </TooltipTrigger>
      <TooltipContent>{t("leaderboard.notQualifiedHint")}</TooltipContent>
    </Tooltip>
  );
}

/** Whole euros normally, but sub-euro amounts get cents rather than a "€0" that looks like a bug. */
function money(n: number, currency: string): string {
  return formatMoney(n, currency, n > 0 && n < 1 ? 2 : 0);
}
